// src/account/get-web-authn-account.ts
import { concatHex, toHex as toHex2 } from "viem";
import { toWebAuthnAccount as toWebAuthnAccount2 } from "viem/account-abstraction";

// src/provider-injection/injection.ts
var isGianoProviderInjection = (injection) => {
  if (typeof injection !== "object" || !injection) {
    return false;
  }
  const typed = injection;
  return typeof typed.getNameForCredential === "function" && typeof typed.getCredentialInfo === "function" && typeof typed.onCredentialCreated === "function" && typeof typed.encodeUserId === "function" && typeof typed.decodeUserId === "function" && typeof typed.onCredentialSignedIn === "function" && typeof typed.getPublicKeyByCredentialId === "function" && typeof typed.onCredentialKey === "function" && (typeof typed.submitUserOperation === "function" || typeof typed.submitUserOperation === "undefined");
};

// src/provider-injection/types/buffer-source.ts
var isBufferSource = (value) => {
  return value instanceof ArrayBuffer || value instanceof Int8Array || value instanceof Uint8Array || value instanceof Uint8ClampedArray || value instanceof Int16Array || value instanceof Uint16Array || value instanceof Int32Array || value instanceof Uint32Array || value instanceof Float32Array || value instanceof Float64Array || value instanceof BigInt64Array || value instanceof BigUint64Array || value instanceof DataView;
};
function isNonEmptyBufferSource(value) {
  return isBufferSource(value) && value.byteLength > 0;
}
function assertNonEmptyBufferSource(value, options) {
  if (!isNonEmptyBufferSource(value)) {
    const errorMessage = (options == null ? void 0 : options.descriptor) ? `\`${options.descriptor}\` must be a non-empty BufferSource` : "Must be a non-empty BufferSource";
    throw new Error(errorMessage);
  }
}

// src/provider-injection/types/credential-info/credential-mediation-requirement.ts
var possibleValues = [
  "conditional",
  "optional",
  "required",
  "silent"
];
var allowedValuesMessage = possibleValues.map((v) => `'${v}'`).join(", ");
function assertCredentialMediationRequirement(value, options) {
  if (typeof value === "string" && possibleValues.includes(value)) {
    return;
  }
  const errorMessage = (options == null ? void 0 : options.descriptor) ? `${options.descriptor} must be one of the following literals: ${allowedValuesMessage}` : `Credential Mediation Requirement must be one of the following literals: ${allowedValuesMessage}`;
  throw new Error(errorMessage);
}

// src/provider-injection/types/credential-info/user-verification-requirement.ts
var possibleValues2 = [
  "discouraged",
  "preferred",
  "required"
];
var allowedValuesMessage2 = possibleValues2.map((v) => `'${v}'`).join(", ");
function assertUserVerificationRequirement(value, options) {
  if (typeof value === "string" && possibleValues2.includes(value)) {
    return;
  }
  const errorMessage = (options == null ? void 0 : options.descriptor) ? `${options.descriptor} must be one of the following literals: ${allowedValuesMessage2}` : `User Verification Requirement must be one of the following literals: ${allowedValuesMessage2}`;
  throw new Error(errorMessage);
}

// src/provider-injection/types/credential-info/base-credential-info.ts
function assertBaseCredentialInfo(info) {
  if (typeof info !== "object" || info === null) {
    throw new Error("Credential info must be an object");
  }
  const {
    userVerification,
    mediation,
    challenge,
    address
  } = info;
  if (userVerification !== void 0) {
    assertUserVerificationRequirement(userVerification, {
      descriptor: "userVerification"
    });
  }
  if (mediation !== void 0) {
    assertCredentialMediationRequirement(mediation, {
      descriptor: "mediation"
    });
  }
  assertNonEmptyBufferSource(challenge, { descriptor: "challenge" });
  if (address !== void 0 && !/^0x[0-9a-fA-F]{40}$/.test(address)) {
    throw new Error("Credential info address must be a 0x-prefixed 20-byte hex address");
  }
}

// src/provider-injection/types/credential-info/resident-key-requirement.ts
var possibleValues3 = [
  "discouraged",
  "preferred",
  "required"
];
var allowedValuesMessage3 = possibleValues3.map((v) => `'${v}'`).join(", ");
function assertResidentKeyRequirement(value, options) {
  if (typeof value === "string" && possibleValues3.includes(value)) {
    return;
  }
  const errorMessage = (options == null ? void 0 : options.descriptor) ? `${options.descriptor} must be one of the following literals: ${allowedValuesMessage3}` : `Resident Key Requirement must be one of the following literals: ${allowedValuesMessage3}`;
  throw new Error(errorMessage);
}

// src/provider-injection/types/credential-info/credential-info.ts
function assertCredentialInfo(value) {
  if (typeof value !== "object" || value === null) {
    throw new Error("Credential info must be an object");
  }
  assertBaseCredentialInfo(value);
  const info = value;
  const { credentialId } = info;
  if (credentialId === null) {
    if (info.residentKey !== void 0) {
      assertResidentKeyRequirement(info.residentKey, { descriptor: "residentKey" });
    }
    return;
  }
  if (credentialId === "user-pick" || isNonEmptyBufferSource(credentialId) || // array of non-empty BufferSource items
  credentialId instanceof Array && credentialId.every((x) => isNonEmptyBufferSource(x))) {
    return;
  }
  throw new Error("`credentialId` must be one of: `'user-pick'`, `null`, non-empty `BufferSource`, or an array of non-empty `BufferSource`");
}

// src/provider-injection/types/credential-info/defaults.ts
var DEFAULT_CREDENTIAL_MEDIATION_REQUIREMENT = "silent";
var DEFAULT_RESIDENT_KEY_REQUIREMENT = "preferred";
var DEFAULT_USER_VERIFICATION_REQUIREMENT = "required";

// src/provider.ts
import {
  createPublicClient,
  parseGwei,
  toHex
} from "viem";
import { createWebAuthnCredential, toWebAuthnAccount } from "viem/account-abstraction";

// src/giano-entry-point.ts
import { entryPoint07Address } from "viem/account-abstraction";
var GianoEntryPointVersion = "0.7";
var GianoEntryPointAddress = entryPoint07Address;

// src/provider-injection/_with-validation/validation-hooks.ts
var validationHooks = Object.freeze({
  getNameForCredential: async function(hookKey, ...args) {
    const hookFn = this.implementation[hookKey];
    const name = await hookFn.apply(this.implementation, args);
    if (typeof name !== "string" || name.length === 0) {
      throw new Error("The credential name must be a non-empty string");
    }
    return name;
  },
  getCredentialInfo: async function(hookKey, ...args) {
    const hookFn = this.implementation[hookKey];
    const info = await hookFn.apply(this.implementation, args);
    assertCredentialInfo(info);
    return info;
  },
  onCredentialCreated: async function(hookKey, ...args) {
    const hookFn = this.implementation[hookKey];
    const address = await hookFn.apply(this.implementation, args);
    if (address === null) {
      return null;
    }
    assertHexAddress(address, { descriptor: "The wallet address" });
    return address;
  },
  encodeUserId: async function(hookKey, ...args) {
    const hookFn = this.implementation[hookKey];
    const encodedUserId = await hookFn.apply(this.implementation, args);
    assertNonEmptyBufferSource(encodedUserId, {
      descriptor: "The encoded user ID"
    });
    return encodedUserId;
  },
  decodeUserId: async function(hookKey, ...args) {
    const hookFn = this.implementation[hookKey];
    const decoded = await hookFn.apply(this.implementation, args);
    assertDecodedUserId(decoded);
    return decoded;
  },
  onCredentialSignedIn: async function(hookKey, ...args) {
    const hookFn = this.implementation[hookKey];
    const value = await hookFn.apply(this.implementation, args);
    if (typeof value !== "boolean") {
      throw new Error("onCredentialSignedIn must return a boolean");
    }
    return value;
  },
  getPublicKeyByCredentialId: async function(hookKey, ...args) {
    const hookFn = this.implementation[hookKey];
    const xyVector = await hookFn.apply(this.implementation, args);
    assertXYVector(xyVector);
    return xyVector;
  },
  onCredentialKey: async function(hookKey, ...args) {
    const hookFn = this.implementation[hookKey];
    return await hookFn.apply(this.implementation, args);
  },
  submitUserOperation: async function(hookKey, ...args) {
    const hookFn = this.implementation[hookKey];
    const hash = await hookFn.apply(this.implementation, args);
    assertHexHash(hash, { descriptor: "User operation hash" });
    return hash;
  }
});

// src/provider-injection/_with-validation/with-validation.ts
var withValidation = (implementation) => {
  if (!isGianoProviderInjection(implementation)) {
    throw new Error("Invalid Giano provider injection implementation");
  }
  const injection = { implementation };
  for (const key of Object.keys(validationHooks)) {
    if (implementation[key] === void 0) {
      injection[key] = void 0;
      continue;
    }
    injection[key] = validationHooks[key].bind(injection, key);
  }
  return injection;
};

// src/provider.ts
import { v4 as uuidv4 } from "uuid";
var ChainType = /* @__PURE__ */ ((ChainType3) => {
  ChainType3[ChainType3["HARDHAT"] = 0] = "HARDHAT";
  return ChainType3;
})(ChainType || {});
var isChainType = (x) => {
  return typeof x === "number" && Object.values(ChainType).includes(x);
};
function extractXYCoords(key) {
  if (key instanceof Uint8Array) {
    key = toHex(key.slice(-64), { size: 64 });
  }
  return { x: `0x${key.slice(-128, -64)}`, y: `0x${key.slice(-64)}` };
}
var createGianoProvider = (options) => {
  const injection = withValidation(options.injection);
  const {
    transports,
    chains,
    initialChainId,
    bundler,
    gianoSmartWalletFactoryAddress
  } = options;
  let smartAccount;
  let chain;
  let transport;
  let client;
  let staticSignature = null;
  let staticSignatureSignedAt = 0;
  let staticSignatureLifetime = 0n;
  const eventListeners = {};
  const submitUserOperation = async (userOpRequest) => {
    if (injection.submitUserOperation === void 0) {
      return await bundler.sendUserOperation(userOpRequest);
    }
    const estimate = await bundler.estimateUserOperationGas(userOpRequest);
    if (!estimate) {
      throw new Error("Could not estimate user operation");
    }
    const prepared = await bundler.prepareUserOperation({
      ...userOpRequest,
      ...estimate
    });
    const preparedWithGas = {
      ...prepared,
      maxFeePerGas: userOpRequest.maxFeePerGas || parseGwei("200"),
      maxPriorityFeePerGas: userOpRequest.maxPriorityFeePerGas || parseGwei("400")
    };
    const signature = await userOpRequest.account.signUserOperation(preparedWithGas);
    const signedUserOp = {
      ...preparedWithGas,
      sender: await userOpRequest.account.getAddress(),
      signature,
      account: {
        entryPoint: {
          address: GianoEntryPointAddress
        }
      }
    };
    return await injection.submitUserOperation(signedUserOp);
  };
  const ensureAccountDeployed = async () => {
    if (!smartAccount) return false;
    try {
      const accountAddress = await smartAccount.getAddress();
      const accountCode = await client.getCode({ address: accountAddress });
      return !!(accountCode && accountCode !== "0x");
    } catch (error) {
      console.warn("Failed to check account deployment status:", error);
      return false;
    }
  };
  const emit = (event, payload) => {
    var _a;
    (_a = eventListeners[event]) == null ? void 0 : _a.forEach((listener) => listener(payload));
  };
  const methods = {
    eth_accounts: async () => {
      return smartAccount ? [await smartAccount.getAddress()] : [];
    },
    eth_chainId: async () => {
      return `0x${chain.id.toString(16)}`;
    },
    eth_call: async ([call, blockTag]) => {
      console.log("eth_call", { call, blockTag });
      return client.request({ method: "eth_call", params: [call, blockTag] });
    },
    signed_eth_call: async ([call, blockTag]) => {
      if (!smartAccount) {
        console.warn("Smart account not available, falling back to regular call");
        return client.request({ method: "eth_call", params: [call, blockTag] });
      }
      const isDeployed = await ensureAccountDeployed();
      if (!isDeployed) {
        console.warn("Smart account not deployed yet, falling back to regular call");
        return client.request({ method: "eth_call", params: [call, blockTag] });
      }
      if (staticSignatureLifetime === 0n) {
        staticSignatureLifetime = await client.readContract({
          address: await smartAccount.getAddress(),
          abi: smartAccount.abi,
          functionName: "getSignatureLifetime"
        });
      }
      const staticSignatureAge = BigInt(Date.now() - staticSignatureSignedAt * 1e3);
      if (!staticSignature || staticSignatureAge > staticSignatureLifetime * 1000n) {
        const { signature, signedAt } = await smartAccount.signStaticCallPermission();
        staticSignature = signature;
        staticSignatureSignedAt = signedAt;
      }
      const result = await client.readContract({
        abi: smartAccount.abi,
        address: smartAccount.address,
        functionName: "signedStaticCall",
        args: [{ target: call.to, data: call.data, signedAt: BigInt(staticSignatureSignedAt), signature: staticSignature }]
      });
      return result;
    },
    wallet_addEthereumChain: () => {
    },
    wallet_revokePermissions: () => {
      smartAccount = null;
      emit("accountsChanged", []);
      emit("disconnect", {
        code: 4900,
        name: "Disconnected",
        message: "User disconnected",
        details: "User disconnected"
      });
    },
    wallet_switchEthereumChain: (params) => {
      const [{ chainId: chainIdHex }] = params;
      const chainId = parseInt(chainIdHex, 16);
      if (chainId === (chain == null ? void 0 : chain.id)) {
        return;
      }
      const newChain = chains.find((chain2) => chain2.id === chainId);
      if (!newChain) {
        throw new Error(`Unknown chain: ${chainId}`);
      }
      const newTransport = transports == null ? void 0 : transports[newChain.id];
      if (!newTransport) {
        throw new Error("No transport for chain");
      }
      smartAccount = null;
      chain = newChain;
      transport = newTransport;
      client = createPublicClient({ transport, chain });
      emit("chainChanged", `0x${chainId.toString(16)}`);
    },
    eth_requestAccounts: async () => {
      if (smartAccount) {
        return [await smartAccount.getAddress()];
      }
      const credentialInfo = await injection.getCredentialInfo();
      if (credentialInfo.credentialId !== null) {
        const webAuthnAccount = await getWebAuthnAccount(credentialInfo, injection);
        if (!webAuthnAccount) {
          throw new Error("Invalid credential");
        }
        smartAccount = await toGianoSmartAccount({
          client,
          owners: [webAuthnAccount],
          factoryAddress: gianoSmartWalletFactoryAddress,
          address: credentialInfo.address
        });
        const smartAccountAddress2 = await smartAccount.getAddress();
        emit("connect", { chainId: `0x${chain.id.toString(16)}` });
        emit("accountsChanged", [smartAccountAddress2]);
        return [smartAccountAddress2];
      }
      const credentialName = await injection.getNameForCredential();
      const chainId = `0x${chain.id.toString(16)}`;
      const residentKey = credentialInfo.residentKey ?? DEFAULT_RESIDENT_KEY_REQUIREMENT;
      const userVerification = credentialInfo.userVerification ?? DEFAULT_USER_VERIFICATION_REQUIREMENT;
      const credential = await createWebAuthnCredential({
        user: {
          name: credentialName,
          id: await injection.encodeUserId(
            uuidv4().replace(/-/g, ""),
            gianoSmartWalletFactoryAddress,
            chainId,
            0 /* HARDHAT */
          )
        },
        challenge: credentialInfo.challenge,
        authenticatorSelection: {
          userVerification,
          residentKey,
          requireResidentKey: residentKey === "required"
        }
      });
      const handlerCreatedAddress = await injection.onCredentialCreated(
        credentialName,
        credentialInfo.challenge,
        credential.raw
      );
      if (handlerCreatedAddress) {
        smartAccount = await toGianoSmartAccount({
          client,
          owners: [toWebAuthnAccount({ credential })],
          address: handlerCreatedAddress,
          factoryAddress: gianoSmartWalletFactoryAddress
        });
        emit("connect", { chainId });
        emit("accountsChanged", [handlerCreatedAddress]);
        return [handlerCreatedAddress];
      }
      smartAccount = await toGianoSmartAccount({
        client,
        owners: [toWebAuthnAccount({ credential })],
        factoryAddress: gianoSmartWalletFactoryAddress
      });
      const smartAccountAddress = await smartAccount.getAddress();
      const xyVector = extractXYCoords(credential.publicKey);
      const accountAddress = await smartAccount.getAddress();
      const accountCode = await client.getCode({ address: accountAddress });
      const isDeployed = accountCode && accountCode !== "0x";
      if (!isDeployed) {
        const deploymentTx = {
          to: accountAddress,
          value: "0x0",
          data: "0x"
          // Empty data
        };
        try {
          console.log("Deploying smart account...");
          const deploymentHash = await methods.eth_sendTransaction([deploymentTx]);
          console.log("Smart account deployment submitted with hash:", deploymentHash);
          const deploymentReceipt = await bundler.waitForUserOperationReceipt({ hash: deploymentHash });
          console.log("Smart account deployed successfully:", deploymentReceipt);
        } catch (error) {
          console.warn("Failed to deploy smart account:", error);
        }
      } else {
        console.log("Smart account already deployed");
      }
      await injection.onCredentialKey(credential.raw.rawId, xyVector);
      emit("connect", { chainId: `0x${chain.id.toString(16)}` });
      emit("accountsChanged", [smartAccountAddress]);
      return [smartAccountAddress];
    },
    eth_sendTransaction: async (calls) => {
      if (!smartAccount) {
        throw new Error("Giano not connected");
      }
      return await submitUserOperation({ calls, account: smartAccount });
    },
    waitForUserOperationReceipt: async ([hash]) => {
      return bundler.waitForUserOperationReceipt({ hash });
    },
    personal_sign: async ([message, address]) => {
      if (!smartAccount) {
        throw new Error("Giano not connected");
      }
      const accountAddress = await smartAccount.getAddress();
      if (address.toLowerCase() !== accountAddress.toLowerCase()) {
        throw new Error("Address mismatch");
      }
      const messageBytes = message.startsWith("0x") ? message : toHex(message);
      return smartAccount.signMessage({ message: messageBytes });
    },
    eth_sign: async ([address, message]) => {
      if (!smartAccount) {
        throw new Error("Giano not connected");
      }
      const accountAddress = await smartAccount.getAddress();
      if (address.toLowerCase() !== accountAddress.toLowerCase()) {
        throw new Error("Address mismatch");
      }
      return smartAccount.signMessage({ message });
    },
    eth_signTypedData: async ([address, typedData]) => {
      console.log("eth_signTypedData", { address, typedData });
      if (!smartAccount) {
        throw new Error("Giano not connected");
      }
      const accountAddress = await smartAccount.getAddress();
      if (address.toLowerCase() !== accountAddress.toLowerCase()) {
        throw new Error("Address mismatch");
      }
      return smartAccount.signTypedData(typedData);
    },
    eth_signTypedData_v4: async ([address, typedData]) => {
      console.log("eth_signTypedData_v4", { address, typedData });
      if (!smartAccount) {
        throw new Error("Giano not connected");
      }
      const accountAddress = await smartAccount.getAddress();
      if (address.toLowerCase() !== accountAddress.toLowerCase()) {
        throw new Error("Address mismatch");
      }
      const parsedTypedData = typeof typedData === "string" ? JSON.parse(typedData) : typedData;
      return smartAccount.signTypedData(parsedTypedData);
    },
    eth_signUserOperation: async ([userOp]) => {
      console.log("eth_signUserOperation", { userOp });
      if (!smartAccount) {
        throw new Error("Giano not connected");
      }
      return smartAccount.signUserOperation({ ...userOp });
    },
    eth_sendSignedUserOperation: async ([signedUserOp]) => {
      console.log("eth_sendSignedUserOperation", { signedUserOp });
      if (!smartAccount) {
        throw new Error("Giano not connected");
      }
      return await submitUserOperation({
        account: smartAccount,
        ...signedUserOp
      });
    },
    eth_prepareUserOperation: async ([calls, options2 = {}]) => {
      console.log("eth_prepareUserOperation", { calls, options: options2 });
      if (!smartAccount) {
        throw new Error("Giano not connected");
      }
      const op = {
        calls,
        ...options2
      };
      const estimate = await bundler.estimateUserOperationGas({ account: smartAccount, ...op });
      if (!estimate) {
        throw new Error("Could not estimate user operation");
      }
      const prepared = await bundler.prepareUserOperation({
        account: smartAccount,
        ...op,
        ...estimate
      });
      return {
        ...prepared,
        preVerificationGas: prepared.preVerificationGas,
        maxFeePerGas: options2.maxFeePerGas || parseGwei("200"),
        maxPriorityFeePerGas: options2.maxPriorityFeePerGas || parseGwei("400")
      };
    }
  };
  methods.wallet_switchEthereumChain([{ chainId: initialChainId.toString(16) }]);
  const provider = {
    getSmartAccount: () => smartAccount,
    request: async (args) => {
      const { method, params } = args;
      if (!(method in methods)) {
        return client.request({ ...args });
      }
      try {
        const response = await methods[method](params);
        return response;
      } catch (e) {
        console.error(e);
        throw e;
      }
    },
    on: (event, listener) => {
      if (!eventListeners[event]) {
        eventListeners[event] = /* @__PURE__ */ new Set();
      }
      eventListeners[event].add(listener);
      return provider;
    },
    removeListener: (event, listener) => {
      var _a;
      (_a = eventListeners[event]) == null ? void 0 : _a.delete(listener);
      return provider;
    }
  };
  return {
    gianoClient: client,
    gianoProvider: provider
  };
};

// src/provider-injection/types/hex/assert-hex.ts
import { isHex } from "viem";

// src/provider-injection/types/hex/length.ts
function getHexAssertionLength(options) {
  if ("digits" in options && options.digits !== void 0) {
    if (typeof options.digits !== "number") {
      throw new Error("`options.digits` must be a number");
    }
    return {
      digits: options.digits,
      originalUnit: "digit"
    };
  }
  if ("bytes" in options && options.bytes !== void 0) {
    if (typeof options.bytes !== "number") {
      throw new Error("`options.bytes` must be a number");
    }
    return {
      digits: options.bytes * 2,
      originalUnit: "byte"
    };
  }
  return void 0;
}
function getHexAssertionLengthMessage(length) {
  if (length === void 0) {
    return "hex";
  }
  const valueInOriginalUnit = length.digits / (length.originalUnit === "digit" ? 1 : 2);
  return `${valueInOriginalUnit}-${length.originalUnit} hex`;
}

// src/provider-injection/types/hex/assert-hex.ts
function assertHex2(value, options = {}) {
  const lengthAssertion = getHexAssertionLength(options);
  if (typeof value === "string" && (!lengthAssertion || value.length === lengthAssertion.digits + 2) && // + '0x' prefix
  isHex(value, { strict: true })) {
    return;
  }
  const lengthMessage = getHexAssertionLengthMessage(lengthAssertion);
  const errorMessage = options.descriptor ? `${options.descriptor} must be a valid ${lengthMessage} string` : `Must be a valid ${lengthMessage} string`;
  throw new Error(errorMessage);
}

// src/provider-injection/types/hex/common.ts
function assertHexAddress(value, options) {
  assertHex2(value, { ...options, bytes: 20 });
}
function assertHexHash(value, options) {
  assertHex2(value, { ...options, bytes: 32 });
}

// src/provider-injection/types/decoded-user-id.ts
function assertDecodedUserId(value) {
  if (typeof value !== "object" || value === null) {
    throw new Error("Decoded user ID must be an object");
  }
  const {
    userId,
    walletFactoryAddress,
    chainId,
    chainType
  } = value;
  if (typeof userId !== "string" || userId.length === 0) {
    throw new Error("`DecodedUserId.userId` must be a non-empty string");
  }
  assertHexAddress(walletFactoryAddress, {
    descriptor: "`DecodedUserId.walletFactoryAddress`"
  });
  if (typeof chainId !== "number") {
    throw new Error("`DecodedUserId.chainId` must be a number");
  }
  if (!isChainType(chainType)) {
    throw new Error("`DecodedUserId.chainType` must be a valid chain type");
  }
}

// src/provider-injection/types/xy-vector.ts
function assertXYVector(value) {
  if (typeof value !== "object" || value === null) {
    throw new Error("XY vector must be an object");
  }
  const { x, y } = value;
  assertHex2(x, { bytes: 32, descriptor: "X-coordinate" });
  assertHex2(y, { bytes: 32, descriptor: "Y-coordinate" });
}

// src/account/get-credential.ts
var getAllowCredentials = (credentialId) => {
  if (credentialId === "user-pick") {
    return void 0;
  }
  const allowedIds = credentialId instanceof Array ? credentialId : [credentialId];
  return allowedIds.map((id) => ({ type: "public-key", id }));
};
var getCredential = async (options) => {
  const requestOptions = {
    mediation: options.mediation ?? DEFAULT_CREDENTIAL_MEDIATION_REQUIREMENT,
    publicKey: {
      challenge: options.challenge,
      userVerification: options.userVerification ?? DEFAULT_USER_VERIFICATION_REQUIREMENT,
      allowCredentials: getAllowCredentials(options.credentialId)
    }
  };
  return await navigator.credentials.get(requestOptions);
};

// src/account/get-web-authn-account.ts
var getWebAuthnAccount = async (options, injection) => {
  try {
    const credential = await getCredential(options);
    if (!credential) {
      return null;
    }
    const signInSuccess = await injection.onCredentialSignedIn(credential, options.challenge);
    if (!signInSuccess) {
      throw new Error("Failed to sign in with credential");
    }
    const { x, y } = await injection.getPublicKeyByCredentialId(credential.rawId);
    if (x === toHex2(0, { size: 32 })) {
      throw new Error("Unknown credential ID");
    }
    return toWebAuthnAccount2({
      credential: {
        id: credential.id,
        publicKey: concatHex([x, y])
      }
    });
  } catch (error) {
    if (["NotAllowedError", "AbortError"].includes(error.name)) {
      return null;
    }
    throw error;
  }
};

// src/account/toGianoSmartAccount.ts
import { gianoSmartWalletAbi, gianoSmartWalletFactoryAbi } from "@appliedblockchain/giano-contracts";
import * as Signature from "ox/Signature";
import {
  BaseError,
  concatHex as concatHex2,
  decodeFunctionData,
  encodeAbiParameters,
  encodeFunctionData,
  encodePacked,
  getAbiItem,
  hashMessage,
  hashTypedData,
  keccak256,
  pad,
  parseSignature,
  size,
  stringToHex,
  toFunctionSelector,
  toHex as toHex3
} from "viem";
import { entryPoint07Abi } from "viem/account-abstraction";
import { entryPoint07Address as entryPoint07Address2, getUserOperationHash, toSmartAccount } from "viem/account-abstraction";
import { readContract } from "viem/actions";
async function toGianoSmartAccount(parameters) {
  const { client, owners, nonce = 0n, factoryAddress } = parameters;
  let address = parameters.address;
  const entryPoint = {
    abi: entryPoint07Abi,
    address: entryPoint07Address2,
    version: "0.7"
  };
  const factory = {
    abi: factoryAbi,
    address: factoryAddress
  };
  const owners_bytes = owners.map((owner2) => {
    if (typeof owner2 === "string") return pad(owner2);
    if (owner2.type === "webAuthn") return owner2.publicKey;
    if (owner2.type === "local") return pad(owner2.address);
    throw new BaseError("invalid owner type");
  });
  const owner = (() => {
    const owner2 = owners[0];
    if (typeof owner2 === "string") return { address: owner2, type: "address" };
    return owner2;
  })();
  const ownerBytes = owners_bytes[0];
  async function signStaticCallPermission() {
    const item = getAbiItem({ abi: gianoSmartWalletAbi, name: "signedStaticCall" });
    const signedAt = Math.floor(Date.now() / 1e3);
    const hash = keccak256(concatHex2([toFunctionSelector(item), toHex3(signedAt, { size: 32 })]));
    const signature = await this.sign({ hash });
    return {
      signature,
      signedAt
    };
  }
  return toSmartAccount({
    client,
    entryPoint,
    async decodeCalls(data) {
      const result = decodeFunctionData({
        abi,
        data
      });
      if (result.functionName === "execute") return [{ to: result.args[0], value: result.args[1], data: result.args[2] }];
      if (result.functionName === "executeBatch")
        return result.args[0].map((arg) => ({
          to: arg.target,
          value: arg.value,
          data: arg.data
        }));
      throw new BaseError(`unable to decode calls for "${result.functionName}"`);
    },
    async encodeCalls(calls) {
      if (calls.length === 1)
        return encodeFunctionData({
          abi,
          functionName: "execute",
          args: [calls[0].to, calls[0].value ?? 0n, calls[0].data ?? "0x"]
        });
      return encodeFunctionData({
        abi,
        functionName: "executeBatch",
        args: [
          calls.map((call) => ({
            data: call.data ?? "0x",
            target: call.to,
            value: call.value ?? 0n
          }))
        ]
      });
    },
    async getAddress() {
      address ??= await readContract(client, {
        ...factory,
        functionName: "getAddress",
        args: [owners_bytes, nonce]
      });
      return address;
    },
    async getFactoryArgs() {
      const factoryData = encodeFunctionData({
        abi: factory.abi,
        functionName: "createAccount",
        args: [owners_bytes, nonce]
      });
      return { factory: factory.address, factoryData };
    },
    async getStubSignature() {
      if (owner.type === "webAuthn")
        return "0x0000000000000000000000000000000000000000000000000000000000000020000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000400000000000000000000000000000000000000000000000000000000000000200000000000000000000000000000000000000000000000000000000000000002000000000000000000000000000000000000000000000000000000000000000c0000000000000000000000000000000000000000000000000000000000000012000000000000000000000000000000000000000000000000000000000000000170000000000000000000000000000000000000000000000000000000000000001949fc7c88032b9fcb5f6efc7a7b8c63668eae9871b765e23123bb473ff57aa831a7c0d9276168ebcc29f2875a0239cffdf2a9cd1c2007c5c77c071db9264df1d000000000000000000000000000000000000000000000000000000000000002549960de5880e8c687434170f6476605b8fe4aeb9a28632c7995cf3ba831d97630500000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000008a7b2274797065223a22776562617574686e2e676574222c226368616c6c656e6765223a2273496a396e6164474850596759334b7156384f7a4a666c726275504b474f716d59576f4d57516869467773222c226f726967696e223a2268747470733a2f2f7369676e2e636f696e626173652e636f6d222c2263726f73734f726967696e223a66616c73657d00000000000000000000000000000000000000000000";
      return wrapSignature({
        ownerBytes,
        signature: "0xfffffffffffffffffffffffffffffff0000000000000000000000000000000007aaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa1c"
      });
    },
    async sign(parameters2) {
      const address2 = await this.getAddress();
      const typedData = toReplaySafeTypedData({
        address: address2,
        chainId: client.chain.id,
        hash: parameters2.hash
      });
      if (owner.type === "address") throw new Error("owner cannot sign");
      const signature = await signTypedData({ owner, typedData });
      return wrapSignature({
        ownerBytes,
        signature
      });
    },
    async signMessage(parameters2) {
      const { message } = parameters2;
      const address2 = await this.getAddress();
      const typedData = toReplaySafeTypedData({
        address: address2,
        chainId: client.chain.id,
        hash: hashMessage(message)
      });
      if (owner.type === "address") throw new Error("owner cannot sign");
      const signature = await signTypedData({ owner, typedData });
      return wrapSignature({
        ownerBytes,
        signature
      });
    },
    async signTypedData(parameters2) {
      const { domain, types, primaryType, message } = parameters2;
      const address2 = await this.getAddress();
      const typedData = toReplaySafeTypedData({
        address: address2,
        chainId: client.chain.id,
        hash: hashTypedData({
          domain,
          message,
          primaryType,
          types
        })
      });
      if (owner.type === "address") throw new Error("owner cannot sign");
      const signature = await signTypedData({ owner, typedData });
      return wrapSignature({
        ownerBytes,
        signature
      });
    },
    async signUserOperation(parameters2) {
      const { chainId = client.chain.id, ...userOperation } = parameters2;
      const address2 = await this.getAddress();
      const hash = getUserOperationHash({
        chainId,
        entryPointAddress: entryPoint.address,
        entryPointVersion: entryPoint.version,
        userOperation: {
          ...userOperation,
          sender: address2
        }
      });
      if (owner.type === "address") throw new Error("owner cannot sign");
      const signature = await sign({ hash, owner });
      return wrapSignature({
        ownerBytes,
        signature
      });
    },
    signStaticCallPermission,
    userOperation: {
      async estimateGas(userOperation) {
        if (owner.type !== "webAuthn") return;
        return {
          verificationGasLimit: BigInt(Math.max(Number(userOperation.verificationGasLimit ?? 0n), 8e5))
        };
      }
    },
    extend: { abi, factory, signStaticCallPermission }
  });
}
async function signTypedData({ typedData, owner }) {
  if (owner.type === "local" && owner.signTypedData) return owner.signTypedData(typedData);
  const hash = hashTypedData(typedData);
  return sign({ hash, owner });
}
async function sign({ hash, owner }) {
  if (owner.type === "webAuthn") {
    const { signature, webauthn } = await owner.sign({
      hash
    });
    return toWebAuthnSignature({ signature, webauthn });
  }
  if (owner.sign) return owner.sign({ hash });
  throw new BaseError("`owner` does not support raw sign.");
}
function toReplaySafeTypedData({ address, chainId, hash }) {
  return {
    domain: {
      chainId,
      name: "Giano Smart Wallet",
      verifyingContract: address,
      version: "1"
    },
    types: {
      GianoSmartWalletMessage: [
        {
          name: "hash",
          type: "bytes32"
        }
      ]
    },
    primaryType: "GianoSmartWalletMessage",
    message: {
      hash
    }
  };
}
function toWebAuthnSignature({ webauthn, signature }) {
  const { r, s } = Signature.fromHex(signature);
  return encodeAbiParameters(
    [
      {
        components: [
          {
            name: "authenticatorData",
            type: "bytes"
          },
          { name: "clientDataJSON", type: "bytes" },
          { name: "challengeIndex", type: "uint256" },
          { name: "typeIndex", type: "uint256" },
          {
            name: "r",
            type: "uint256"
          },
          {
            name: "s",
            type: "uint256"
          }
        ],
        type: "tuple"
      }
    ],
    [
      {
        authenticatorData: webauthn.authenticatorData,
        clientDataJSON: stringToHex(webauthn.clientDataJSON),
        challengeIndex: BigInt(webauthn.challengeIndex),
        typeIndex: BigInt(webauthn.typeIndex),
        r,
        s
      }
    ]
  );
}
function wrapSignature(parameters) {
  const { ownerBytes } = parameters;
  const signatureData = (() => {
    if (size(parameters.signature) !== 65) return parameters.signature;
    const signature = parseSignature(parameters.signature);
    return encodePacked(["bytes32", "bytes32", "uint8"], [signature.r, signature.s, signature.yParity === 0 ? 27 : 28]);
  })();
  return encodeAbiParameters(
    [
      {
        components: [
          {
            name: "ownerBytes",
            type: "bytes"
          },
          {
            name: "signatureData",
            type: "bytes"
          }
        ],
        type: "tuple"
      }
    ],
    [
      {
        ownerBytes,
        signatureData
      }
    ]
  );
}
var abi = gianoSmartWalletAbi;
var factoryAbi = gianoSmartWalletFactoryAbi;

// src/connector.ts
import { createConnector } from "wagmi";
function createGianoConnector({ provider }) {
  return createConnector(({ chains }) => {
    const connector = {
      id: "giano",
      name: "Giano Connector",
      type: "custom",
      connect: async () => {
        const accounts = await provider.request({ method: "eth_requestAccounts" });
        const chainId = await connector.getChainId();
        return { accounts, chainId };
      },
      disconnect: async () => {
        await provider.request({ method: "wallet_revokePermissions", params: [{ eth_accounts: [] }] });
      },
      getAccounts: async () => {
        return provider.request({ method: "eth_accounts" });
      },
      getProvider: async () => {
        return provider;
      },
      isAuthorized: async () => {
        try {
          const accounts = await connector.getAccounts();
          return accounts.length > 0;
        } catch {
          return false;
        }
      },
      setup: async () => {
      },
      switchChain: async ({ chainId }) => {
        await provider.request({ method: "wallet_switchEthereumChain", params: [{ chainId: `0x${chainId.toString(16)}` }] });
        return chains.find((chain) => chain.id === chainId);
      },
      getChainId: async () => {
        const chainId = await provider.request({ method: "eth_chainId" });
        return parseInt(chainId, 16);
      },
      onAccountsChanged: () => {
      },
      onChainChanged: () => {
      },
      onDisconnect: () => {
      },
      waitForUserOperationReceipt: async (hash) => {
        return provider.request({
          method: "waitForUserOperationReceipt",
          params: [hash]
        });
      }
    };
    return connector;
  });
}

// src/gianoWallet.ts
var giano = ({ provider }) => () => ({
  id: "giano",
  name: "Giano",
  iconUrl: "data:image/svg+xml;base64,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",
  iconBackground: "#ffffff",
  createConnector: () => {
    return createGianoConnector({ provider });
  }
});
export {
  ChainType,
  DEFAULT_CREDENTIAL_MEDIATION_REQUIREMENT,
  DEFAULT_RESIDENT_KEY_REQUIREMENT,
  DEFAULT_USER_VERIFICATION_REQUIREMENT,
  GianoEntryPointAddress,
  GianoEntryPointVersion,
  assertCredentialInfo,
  assertDecodedUserId,
  assertHex2 as assertHex,
  assertHexAddress,
  assertHexHash,
  assertNonEmptyBufferSource,
  assertXYVector,
  createGianoConnector,
  createGianoProvider,
  getWebAuthnAccount,
  giano,
  isBufferSource,
  isChainType,
  isGianoProviderInjection,
  isNonEmptyBufferSource,
  sign,
  signTypedData,
  toGianoSmartAccount,
  toReplaySafeTypedData,
  toWebAuthnSignature,
  wrapSignature
};
